import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

// recoil
import { useTokenState } from "../recoil/token";

// api
import { getUserCoinCenter, CoinCenter } from "../api/coin";
import {
  postStartArcadeGameSession,
  postCompleteArcadeGameSession,
  ArcadeGameSession,
  getArcadeGames,
  ArcadeGame,
  getArcadeGameDetail,
} from "../api/arcade";
import { getCurrentUser } from "../api/user";

interface AuthContextValue {
  coinCenter?: CoinCenter;
  fetchCoinCenter: () => Promise<void>;
  arcadeGameSession?: ArcadeGameSession;
  startArcadeGameSession: (arcadeGameId: string) => Promise<void>;
  endArcadeGameSession: (data: { [key: string]: any }) => Promise<void>;
  arcadeGames: ArcadeGame[];
  fetchArcadeGames: () => Promise<void>;
  arcadeGameDetail: ArcadeGame | undefined;
  loadArcadeGameDetail: (arcadeGameId: string) => Promise<void>;
}

export const useAuth = (): AuthContextValue => {
  return useContext(AuthContext);
};

const AuthContext = createContext({} as AuthContextValue);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [coinCenter, setCoinCenter] = useState<CoinCenter>();
  const [arcadeGames, setArcadeGames] = useState<ArcadeGame[]>([]);
  const [arcadeGameDetail, setArcadeGameDetail] = useState<ArcadeGame>();
  const [arcadeGameSession, setArcadeGameSession] =
    useState<ArcadeGameSession>();

  const [token] = useTokenState();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const getTokenFromUrl = () => {
      const query = new URLSearchParams(location.search);
      return query.get("token");
    };

    const tokenParam = getTokenFromUrl();

    if (tokenParam) {
      localStorage.setItem("token", tokenParam);
      // Remove token from URL
      const currentPath = location.pathname;
      navigate(currentPath, { replace: true });
    }

    if (token || tokenParam) {
      fetchInitialData();
    } else {
      window.location.replace(process.env.REACT_APP_MEMBERS_APP_URL!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInitialData = async () => {
    try {
      await Promise.all([
        fetchCoinCenter(),
        fetchUserProfile(),
        loadArcadeGameDetail(process.env.REACT_APP_FLYING_ELEMENTS_ID!),
      ]);
    } catch (error) {
      toast.error(
        (error as any)?.response?.data?.detail ||
        (error as any)?.response?.data?.errors[0]?.msg ||
        "Something went wrong"
      );
      window.location.replace(process.env.REACT_APP_MEMBERS_APP_URL!);
    }
  };

  const fetchCoinCenter = async () => {
    try {
      const response = await getUserCoinCenter();
      setCoinCenter(response.data.coin_center);
    } catch (error) {
      throw error;
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await getCurrentUser();
      // @ts-ignore
      analytics.identify(response.data.id, {
        email: response.data.email,
        id: response.data.id,
      });
    } catch (error) {
      throw error;
    }
  };

  const startArcadeGameSession = async (arcadeGameId: string) => {
    // @ts-ignore
    analytics.track("start_arcade_game_session", {
      arcade_game_id: arcadeGameId,
    });
    try {
      const response = await postStartArcadeGameSession(arcadeGameId);
      setArcadeGameSession(response.data.arcade_game_session);
    } catch (error) {
      throw error;
    }
  };

  const loadArcadeGameDetail = async (arcadeGameId: string) => {
    try {
      const response = await getArcadeGameDetail(arcadeGameId);
      setArcadeGameDetail(response.data.arcade_game);
    } catch (error) {
      throw error;
    }
  };

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 5000;

  const endArcadeGameSession = async (
    data: { [key: string]: any },
    retryCount = 0
  ): Promise<void> => {
    if (!arcadeGameSession) return;
    // @ts-ignore
    analytics.track("end_arcade_game_session", {
      arcade_game_id: arcadeGameSession.arcade_game_id,
      data,
      coins_earned: data?.coins_earned,
    });

    try {
      await postCompleteArcadeGameSession(
        arcadeGameSession.arcade_game_id,
        arcadeGameSession.id!,
        data
      );
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));

        return endArcadeGameSession(data, retryCount + 1);
      } else {
        throw error;
      }
    }
  };

  const fetchArcadeGames = async () => {
    try {
      const response = await getArcadeGames();
      setArcadeGames(response.data.arcade_games);
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        coinCenter,
        fetchCoinCenter,
        arcadeGameSession,
        startArcadeGameSession,
        endArcadeGameSession,
        arcadeGames,
        fetchArcadeGames,
        arcadeGameDetail,
        loadArcadeGameDetail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
